/**
 * @fileoverview Persistent queue of recordings whose upload hasn't completed.
 *
 * The web recording flow used to discard a captured lesson the moment its
 * upload failed (a stale-consent 403, a network drop) or the tab closed —
 * losing a full lesson. This mirrors the mobile `uploadQueue`: pending
 * recordings (audio Blob + metadata) are persisted so they can be retried.
 *
 * Backed by IndexedDB, the only browser store that reliably holds Blobs across
 * reloads. When IndexedDB is unavailable (SSR, some test envs) it falls back to
 * an in-memory Map so callers can use the same async API unconditionally — the
 * persistence guarantee is simply downgraded to within-session.
 *
 * A browser can be shared (a studio computer), so every entry carries the
 * `accountId` of the instructor who recorded it, and the recorder lists and
 * retries only the signed-in instructor's entries (listForAccount), as the
 * mobile queue does. Entries saved before that field existed have no owner:
 * the next instructor to list the queue adopts them only when no other account
 * owns anything in it; otherwise they stay hidden and stored. Signing out
 * never deletes a recording.
 */

const DB_NAME = "hans_uploads";
const STORE = "pending";
const DB_VERSION = 1;

const hasIDB = typeof indexedDB !== "undefined";
const memory = new Map(); // id -> entry, fallback store

let dbPromise = null;
const openDb = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
};

const reqToPromise = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const makeId = () => `up_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Write one record (upsert by id) to IndexedDB, else to the in-memory store. */
const put = async (record) => {
  if (hasIDB) {
    try {
      const db = await openDb();
      const store = db.transaction(STORE, "readwrite").objectStore(STORE);
      await reqToPromise(store.put(record));
      return record;
    } catch (_e) {
      // Fall through to the in-memory store on any IndexedDB failure.
    }
  }
  memory.set(record.id, record);
  return record;
};

export const uploadQueue = {
  /**
   * Persist a pending recording. Returns the stored entry (with a generated
   * `id` and `createdAt` if not supplied). The caller stamps `accountId`, the
   * recording instructor's user_id, which is kept as given.
   */
  async enqueue(entry) {
    return put({
      ...entry,
      id: entry.id || makeId(),
      createdAt: entry.createdAt || new Date().toISOString(),
      // Why the last send did not finish ({code, message, …}), written by
      // setBlocked after a failure; null while nothing has been tried.
      blocked: entry.blocked || null,
    });
  },

  /**
   * List every pending recording, whoever owns it, oldest first. To show or
   * retry recordings use {@link uploadQueue.listForAccount}.
   */
  async list() {
    const byId = new Map();
    if (hasIDB) {
      try {
        const db = await openDb();
        const store = db.transaction(STORE, "readonly").objectStore(STORE);
        const all = (await reqToPromise(store.getAll())) || [];
        for (const entry of all) byId.set(entry.id, entry);
      } catch (_e) {
        // Fall through to the in-memory store.
      }
    }
    // Merge the in-memory fallback: an entry lands there when a single write
    // failed (e.g. quota exceeded on a large blob) even though reads still
    // work, so IDB results alone would hide it.
    for (const entry of memory.values()) {
      if (!byId.has(entry.id)) byId.set(entry.id, entry);
    }
    return [...byId.values()].sort((a, b) =>
      String(a.createdAt).localeCompare(String(b.createdAt))
    );
  },

  /**
   * The pending recordings this instructor may see and retry, oldest first.
   * First adopts entries with no owner for this account when no other account
   * owns anything in the queue, saving the attribution so a later instructor
   * does not see them; with another owner present they stay hidden. Mirrors
   * the mobile queue's getForAccount.
   * @param {string|null|undefined} accountId - The signed-in instructor's user_id.
   * @returns {Promise<Array<Object>>} Empty when no account is given.
   */
  async listForAccount(accountId) {
    if (!accountId) return [];
    const all = await uploadQueue.list();
    const unowned = all.filter((e) => !e.accountId);
    if (unowned.length > 0 && !all.some((e) => e.accountId && e.accountId !== accountId)) {
      for (const entry of unowned) {
        entry.accountId = accountId;
        await put(entry);
      }
    }
    return all.filter((e) => e.accountId === accountId);
  },

  /**
   * Remember why an entry's last send did not finish, so the Saved recordings
   * page can say what each take waits for without sending it again (iOS
   * review B6). `blocked` is a {@link blockedReason} result, or null once a
   * send is under way again. A missing entry is left alone.
   * @param {string} id
   * @param {{code: string, message?: string|null}|null} blocked
   */
  async setBlocked(id, blocked) {
    const all = await uploadQueue.list();
    const entry = all.find((e) => e.id === id);
    if (!entry) return;
    entry.blocked = blocked
      ? {
          code: blocked.code,
          message: blocked.message || null,
          ...(blocked.context ? { context: blocked.context } : {}),
          ...(blocked.contextName ? { contextName: blocked.contextName } : {}),
          ...(blocked.pendingStudents ? { pendingStudents: blocked.pendingStudents } : {}),
        }
      : null;
    await put(entry);
  },

  /** Remove a pending recording once it has uploaded (or been discarded). */
  async remove(id) {
    memory.delete(id);
    if (hasIDB) {
      try {
        const db = await openDb();
        const store = db.transaction(STORE, "readwrite").objectStore(STORE);
        await reqToPromise(store.delete(id));
      } catch (_e) {
        // Best-effort — the entry is already gone from the in-memory store.
      }
    }
  },
};
